import {
  useMutation,
  UseMutationResult,
  useQueries,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { createMongoFilter, getErrorResponseMessage } from "@via/utils/helpers";
import {
  type ErrorNotification,
  type OperatorMap,
} from "@via/utils/interfaces.tsx";
import { AxiosResponse } from "axios";
import {
  CreateVmetaCollectionRequestBody,
  CreateVmetaConfigApiRequestBody,
  CreateVmetaFieldRequestBody,
  CheckVmetaDataWritePermissionRequestBody,
  CheckVmetaDataWritePermissionResponseBody,
  VmetaConfigApiRequestParams,
  VmetaConfigApiResponseBody,
  VmetaConfigApisResponseBody,
  VmetaCollectionsResponseBody,
  VmetaData,
  VmetaDataListParams,
  VmetaDataRequestParams,
  VmetaDataResponseBody,
  VmetaDatasResponseBody,
  VmetaFieldsResponseBody,
  VmetaCollectionRequestParams,
  UpdateVmetaCollectionRequestBody,
  VmetaCollectionResponseBody,
  VmetaFieldRequestParams,
  UpdateVmetaFieldRequestBody,
  VmetaFieldResponseBody,
  UpdateVmetaConfigApiRequestBody,
} from "common";

export type CheckVmetaCollectionWritePermissionResponseBody = {
  status: string;
  data: {
    writeAllowed: boolean;
    inUseDatasets: { id: string; name: string; fileIds: string[] }[];
  };
};

// Interface for populated search request parameters
interface PopulatedSearchParams {
  canvasId: string;
  collectionName: string;
  filter?:
    | Array<{ id: string; value: unknown }>
    | { or: Array<{ id: string; value: unknown }> }
    | { and: Array<{ id: string; value: unknown }> };
  operatorMap?: OperatorMap;
  sort?: Array<{ id: string; desc: boolean }>;
  order?: "desc" | "asc";
  fields?: string;
  take?: number;
  skip?: number;
}

export const useUpdateVmetaCollection = (): UseMutationResult<
  VmetaCollectionResponseBody,
  ErrorNotification,
  VmetaCollectionRequestParams & UpdateVmetaCollectionRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<
    VmetaCollectionResponseBody,
    ErrorNotification,
    VmetaCollectionRequestParams & UpdateVmetaCollectionRequestBody
  >({
    mutationFn: async (
      payload: VmetaCollectionRequestParams & UpdateVmetaCollectionRequestBody
    ) => {
      const { id, ...body } = payload;
      const res = await axios.patch(`/api/v1/vmeta/collection/${id}`, body);
      return res.data;
    },
    onSuccess: async (): Promise<void> => {
      setNotification({
        id: "update-vmeta-collection-success",
        message: "Metadata Collection Successfully Updated",
        variant: NOTIFICATION.SUCCESS,
      });

      await queryClient.invalidateQueries({
        queryKey: ["/api/v1/vmeta/collection/search"],
      });
    },
    onError: (error: ErrorNotification) => {
      let errorMessage = "";
      if (
        error?.response?.data?.errors &&
        error?.response?.data?.errors.length > 0
      ) {
        errorMessage = error.response.data.errors[0]?.message;
      } else {
        errorMessage = error?.response?.data?.message;
      }
      setNotification({
        id: "update-vmeta-collection-failed",
        message: "Metadata Collection Update Failed",
        details: errorMessage,
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useCreateVmetaCollection = (): UseMutationResult<
  VmetaCollectionResponseBody,
  ErrorNotification,
  CreateVmetaCollectionRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<
    VmetaCollectionResponseBody,
    ErrorNotification,
    CreateVmetaCollectionRequestBody
  >({
    mutationFn: async (payload: CreateVmetaCollectionRequestBody) => {
      const res = await axios.post("/api/v1/vmeta/collection/create", payload);
      return res.data;
    },
    onSuccess: async (): Promise<void> => {
      setNotification({
        id: "create-vmeta-collection-success",
        message: "Metadata Collection Created",
        details: "Metadata Collection has been created",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the vmeta collection list
      await queryClient.invalidateQueries({
        queryKey: ["/api/v1/vmeta/collection/search"],
      });
    },
    onError: (error: ErrorNotification) => {
      let errorMessage = "";
      if (
        error?.response?.data?.errors &&
        error?.response?.data?.errors.length > 0
      ) {
        errorMessage = error.response.data.errors[0]?.message;
      } else {
        errorMessage = error?.response?.data?.message;
      }

      setNotification({
        id: "create-vmeta-collection-failed",
        message: "Metadata Collection Creation Failed",
        details: errorMessage,
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const fetchVmetaCollections = async (
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }>,
  filter?: Array<{ id: string; value: unknown }>
): Promise<VmetaCollectionsResponseBody> => {
  try {
    const baseUrl = "/api/v1/vmeta/collection/search";
    const payload: any = {
      take,
      skip,
    };

    if (sort.length > 0) {
      payload.sort = sort[0].id;
      payload.order = sort[0].desc ? "desc" : "asc";
    }

    if (filter && filter.length > 0) {
      const mongoFilter = createMongoFilter(filter, {
        projectID: "eq",
        name: "regex",
      });
      payload.filter = mongoFilter;
    }

    const res = await axios.post(baseUrl, payload);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useVmetaCollections = (
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = [],
  filter: Array<{ id: string; value: unknown }> = []
): UseQueryResult<VmetaCollectionsResponseBody, Error> => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery<VmetaCollectionsResponseBody, Error>({
    queryKey: ["/api/v1/vmeta/collection/search", take, skip, sort, filter],
    queryFn: async () => await fetchVmetaCollections(take, skip, sort, filter),
    retry: false,
  });
};

export const fetchVmetaFields = async (
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }> = [],
  filter: Array<{ id: string; value: unknown }> = []
): Promise<VmetaFieldsResponseBody> => {
  try {
    const baseUrl = "/api/v1/vmeta/field/search";
    const payload: any = {
      take,
      skip,
    };

    if (sort.length > 0) {
      payload.sort = sort[0].id;
      payload.order = sort[0].desc ? "desc" : "asc";
    }

    if (filter && filter.length > 0) {
      const mongoFilter = createMongoFilter(filter, {
        collectionID: "eq",
        name: "regex",
      });
      payload.filter = mongoFilter;
    }

    const res = await axios.post(baseUrl, payload);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useVmetaFields = (
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = [],
  filter: Array<{ id: string; value: unknown }> = []
): UseQueryResult<VmetaFieldsResponseBody, Error> => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery<VmetaFieldsResponseBody, Error>({
    queryKey: ["/api/v1/vmeta/field/search", take, skip, sort, filter],
    queryFn: async () => await fetchVmetaFields(take, skip, sort, filter),
    retry: false,
  });
};

export const useVmetaFieldsByCollectionIds = (collectionIds: string[]) => {
  return useQueries({
    queries: collectionIds.map(collectionId => ({
      queryKey: [
        "/api/v1/vmeta/field/search",
        1000,
        0,
        [],
        [{ id: "collectionID", value: collectionId }],
      ] as const,
      queryFn: async (): Promise<VmetaFieldsResponseBody> =>
        await fetchVmetaFields(
          1000,
          0,
          [],
          [{ id: "collectionID", value: collectionId }]
        ),
      retry: false,
      enabled: !!collectionId,
    })),
  });
};

export const useCreateVmetaField = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (payload: CreateVmetaFieldRequestBody) => {
      const res = await axios.post("/api/v1/vmeta/field/create", payload);
      return res.data;
    },
    onSuccess: async (): Promise<void> => {
      setNotification({
        id: "create-vmeta-field-success",
        message: "Metadata Field Created",
        details: "Metadata Field has been created",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the vmeta field list
      await queryClient.invalidateQueries({
        queryKey: ["/api/v1/vmeta/field/search"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "create-vmeta-field-failed",
        message: "Metadata Field Creation Failed",
        details:
          error?.response?.data?.message ??
          "Metadata Field could not be created",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const fetchPopulatedVmetaData = (
  canvasId: string,
  collectionName: string,
  searchParams: Omit<PopulatedSearchParams, "canvasId" | "collectionName"> = {}
): Promise<VmetaDatasResponseBody> =>
  postVmetaSearch(
    `/api/v1/vmeta/canvas/${encodeURIComponent(canvasId)}/data/${encodeURIComponent(collectionName)}/search/populated`,
    searchParams
  );

/**
 * Plain search: reference fields come back as the stored parent id. search/populated
 * replaces them with the parent's identifier value, so use this when the id is needed.
 */
export const fetchVmetaData = (
  canvasId: string,
  collectionName: string,
  searchParams: Omit<PopulatedSearchParams, "canvasId" | "collectionName"> = {}
): Promise<VmetaDatasResponseBody> =>
  postVmetaSearch(
    `/api/v1/vmeta/canvas/${encodeURIComponent(canvasId)}/data/${encodeURIComponent(collectionName)}/search`,
    searchParams
  );

const postVmetaSearch = async (
  baseUrl: string,
  searchParams: Omit<PopulatedSearchParams, "canvasId" | "collectionName">
): Promise<VmetaDatasResponseBody> => {
  try {
    const payload: any = {};
    // Add optional parameters if provided
    if (searchParams.sort && searchParams.sort.length > 0) {
      payload.sort = searchParams.sort[0].id;
      payload.order = searchParams.sort[0].desc ? "desc" : "asc";
    }
    if (searchParams.fields) payload.fields = searchParams.fields;
    if (searchParams.take !== undefined) payload.take = searchParams.take;
    if (searchParams.skip !== undefined) payload.skip = searchParams.skip;

    // Handle filter using createMongoFilter (single source of truth)
    if (searchParams.filter) {
      const mongoFilter = createMongoFilter(
        searchParams.filter,
        searchParams.operatorMap || {}
      );
      payload.filter = mongoFilter;
    }

    const res = await axios.post(baseUrl, payload);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const usePopulatedVmetaData = (
  canvasId: string,
  collectionName: string,
  searchParams: Omit<PopulatedSearchParams, "canvasId" | "collectionName"> = {}
): UseQueryResult<VmetaDatasResponseBody, Error> => {
  return useQuery<VmetaDatasResponseBody, Error>({
    queryKey: [
      "/api/v1/vmeta/canvas",
      canvasId,
      "data",
      collectionName,
      "search/populated",
      searchParams,
    ],
    queryFn: async () =>
      await fetchPopulatedVmetaData(canvasId, collectionName, searchParams),
    retry: false,
    enabled: !!canvasId && !!collectionName, // Only run query if both canvasId and collectionName are provided
  });
};

export const useCreateVmetaData = (): UseMutationResult<
  VmetaDataResponseBody,
  ErrorNotification,
  VmetaDataListParams & VmetaData
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<
    VmetaDataResponseBody,
    ErrorNotification,
    VmetaDataListParams & VmetaData
  >({
    mutationFn: async ({
      canvasId,
      collectionName,
      file,
    }: VmetaDataListParams & VmetaData) => {
      const res = await axios.post(
        `/api/v1/vmeta/canvas/${canvasId}/data/${collectionName}/create`,
        file
      );
      return res.data;
    },
    onSuccess: async (): Promise<void> => {
      setNotification({
        id: "create-vmeta-data-success",
        message: "Data Created Successfully",
        variant: NOTIFICATION.SUCCESS,
      });
      await queryClient.invalidateQueries({
        queryKey: ["/api/v1/vmeta/canvas"],
      });
    },
    onError: (error: ErrorNotification) => {
      return error;
    },
  });
};

export const useDeleteVmetaData = (): UseMutationResult<
  void,
  ErrorNotification,
  VmetaDataRequestParams
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<void, ErrorNotification, VmetaDataRequestParams>({
    mutationFn: async ({
      canvasId,
      collectionName,
      id,
    }: VmetaDataRequestParams) => {
      const res = await axios.delete(
        `/api/v1/vmeta/canvas/${canvasId}/data/${collectionName}/${id}`
      );
      return res.data;
    },
    onSuccess: async (): Promise<void> => {
      setNotification({
        id: "delete-vmeta-data-success",
        message: "Data Deleted Successfully",
        variant: NOTIFICATION.SUCCESS,
      });
      await queryClient.invalidateQueries({
        queryKey: ["/api/v1/vmeta/canvas"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "delete-vmeta-data-failed",
        message: "Data Deletion Failed",
        details: getErrorResponseMessage(error) || "",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useCheckVmetaDataWritePermission = (): UseMutationResult<
  CheckVmetaDataWritePermissionResponseBody,
  ErrorNotification,
  VmetaDataListParams & CheckVmetaDataWritePermissionRequestBody
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    CheckVmetaDataWritePermissionResponseBody,
    ErrorNotification,
    VmetaDataListParams & CheckVmetaDataWritePermissionRequestBody
  >({
    mutationFn: async ({
      canvasId,
      collectionName,
      ids,
    }: VmetaDataListParams & CheckVmetaDataWritePermissionRequestBody) => {
      const res = await axios.post(
        `/api/v1/vmeta/canvas/${canvasId}/data/${collectionName}/checkWritePermission`,
        { ids }
      );
      return res.data;
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "check-vmeta-data-write-permission-failed",
        message: "Write Permission Check Failed",
        details: getErrorResponseMessage(error) || "",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useUpdateVmetaData = (): UseMutationResult<
  VmetaDataResponseBody,
  ErrorNotification,
  VmetaDataRequestParams & VmetaData
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    VmetaDataResponseBody,
    ErrorNotification,
    VmetaDataRequestParams & VmetaData
  >({
    mutationFn: async ({
      canvasId,
      collectionName,
      id,
      ...body
    }: VmetaDataRequestParams & VmetaData) => {
      const res = await axios.patch(
        `/api/v1/vmeta/canvas/${canvasId}/data/${collectionName}/${id}`,
        body
      );
      return res.data;
    },
    onSuccess: async (): Promise<void> => {
      setNotification({
        id: "update-vmeta-data-success",
        message: "Data Updated Successfully",
        variant: NOTIFICATION.SUCCESS,
      });
      await queryClient.invalidateQueries({
        queryKey: ["/api/v1/vmeta/canvas"],
      });
    },
    onError: (error: ErrorNotification) => {
      return error;
    },
  });
};

export const useGetVmetaData = (): UseMutationResult<
  VmetaDataResponseBody,
  ErrorNotification,
  VmetaDataRequestParams
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    VmetaDataResponseBody,
    ErrorNotification,
    VmetaDataRequestParams
  >({
    mutationFn: async ({
      canvasId,
      collectionName,
      id,
    }: VmetaDataRequestParams) => {
      const res = await axios.get(
        `/api/v1/vmeta/canvas/${canvasId}/data/${collectionName}/${id}`
      );
      return res.data;
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "get-vmeta-data-failed",
        message: "Data Fetch Failed",
        details: getErrorResponseMessage(error) || "",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useDeleteVmetaCollection = (): UseMutationResult<
  void,
  ErrorNotification,
  VmetaCollectionRequestParams
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<void, ErrorNotification, VmetaCollectionRequestParams>({
    mutationFn: async ({ id }: VmetaCollectionRequestParams) => {
      const res = await axios.delete(`/api/v1/vmeta/collection/${id}`);
      return res.data;
    },
    onSuccess: async (): Promise<void> => {
      await queryClient.invalidateQueries({
        queryKey: ["/api/v1/vmeta/collection/search"],
      });
      setNotification({
        id: "delete-vmeta-collection-success",
        message: "Collection Deleted Successfully",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "delete-vmeta-collection-failed",
        message: "Collection Deletion Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useCheckVmetaCollectionWritePermission = (): UseMutationResult<
  CheckVmetaCollectionWritePermissionResponseBody,
  ErrorNotification,
  VmetaCollectionRequestParams
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<
    CheckVmetaCollectionWritePermissionResponseBody,
    ErrorNotification,
    VmetaCollectionRequestParams
  >({
    mutationFn: async ({ id }: VmetaCollectionRequestParams) => {
      const res = await axios.post(
        `/api/v1/vmeta/collection/${id}/checkWritePermission`
      );
      return res.data;
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "check-vmeta-collection-write-permission-failed",
        message: "Write Permission Check Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useDeleteVmetaField = (): UseMutationResult<
  void,
  ErrorNotification,
  VmetaFieldRequestParams
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<void, ErrorNotification, VmetaFieldRequestParams>({
    mutationFn: async ({ id }: VmetaFieldRequestParams) => {
      const res = await axios.delete(`/api/v1/vmeta/field/${id}`);
      return res.data;
    },
    onSuccess: async (): Promise<void> => {
      await queryClient.invalidateQueries({
        queryKey: ["/api/v1/vmeta/field/search"],
      });
      setNotification({
        id: "delete-vmeta-field-success",
        message: "Field Deleted Successfully",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "delete-vmeta-field-failed",
        message: "Field Deletion Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const fetchVmetaConfigApis = async (
  canvasId: string,
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }> = [],
  filter: Array<{ id: string; value: unknown }> = []
): Promise<VmetaConfigApisResponseBody> => {
  try {
    const baseUrl = `/api/v1/vmeta/canvas/${canvasId}/config/api/search`;
    const payload: any = {
      take,
      skip,
    };

    if (sort.length > 0) {
      payload.sort = sort[0].id;
      payload.order = sort[0].desc ? "desc" : "asc";
    }

    if (filter && filter.length > 0) {
      const mongoFilter = createMongoFilter(filter, {
        collectionID: "eq",
        route: "regex",
      });
      payload.filter = mongoFilter;
    }

    const res = await axios.post(baseUrl, payload);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useVmetaConfigApis = (
  canvasId: string,
  page: number = 0,
  pageSize: number = 100,
  sort: Array<{ id: string; desc: boolean }> = [],
  filter: Array<{ id: string; value: unknown }> = []
): UseQueryResult<VmetaConfigApisResponseBody, Error> => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery<VmetaConfigApisResponseBody, Error>({
    queryKey: [
      "/api/v1/vmeta/canvas/config/api/search",
      canvasId,
      take,
      skip,
      sort,
      filter,
    ],
    queryFn: async () =>
      await fetchVmetaConfigApis(canvasId, take, skip, sort, filter),
    retry: false,
    enabled: !!canvasId,
  });
};

export const useCreateVmetaConfigApi = (): UseMutationResult<
  VmetaConfigApiResponseBody,
  ErrorNotification,
  { canvasId: string; data: CreateVmetaConfigApiRequestBody }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    VmetaConfigApiResponseBody,
    ErrorNotification,
    { canvasId: string; data: CreateVmetaConfigApiRequestBody }
  >({
    mutationFn: async ({ canvasId, data }) => {
      const res = await axios.post(
        `/api/v1/vmeta/canvas/${canvasId}/config/api/create`,
        data
      );
      return res.data;
    },
    onSuccess: async (_, variables): Promise<void> => {
      await queryClient.invalidateQueries({
        queryKey: [
          "/api/v1/vmeta/canvas/config/api/search",
          variables.canvasId,
        ],
      });
      setNotification({
        id: "create-vmeta-config-api-success",
        message: "API Config Created",
        details: "API config has been created successfully.",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      const errorMessage =
        error?.response?.data?.errors?.[0]?.message ||
        error?.response?.data?.message ||
        "API config could not be created";

      setNotification({
        id: "create-vmeta-config-api-failed",
        message: "API Config Creation Failed",
        details: errorMessage,
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useUpdateVmetaConfigApi = (): UseMutationResult<
  VmetaConfigApiResponseBody,
  ErrorNotification,
  {
    canvasId: string;
    id: string;
    data: UpdateVmetaConfigApiRequestBody;
  }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    VmetaConfigApiResponseBody,
    ErrorNotification,
    {
      canvasId: string;
      id: string;
      data: UpdateVmetaConfigApiRequestBody;
    }
  >({
    mutationFn: async ({ canvasId, id, data }) => {
      const res = await axios.patch(
        `/api/v1/vmeta/canvas/${canvasId}/config/api/${id}`,
        data
      );
      return res.data;
    },
    onSuccess: async (_, variables): Promise<void> => {
      await queryClient.invalidateQueries({
        queryKey: [
          "/api/v1/vmeta/canvas/config/api/search",
          variables.canvasId,
        ],
      });
      setNotification({
        id: "update-vmeta-config-api-success",
        message: "API Config Updated",
        details: "API config has been updated successfully.",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      const errorMessage =
        error?.response?.data?.errors?.[0]?.message ||
        error?.response?.data?.message ||
        "API config could not be updated";

      setNotification({
        id: "update-vmeta-config-api-failed",
        message: "API Config Update Failed",
        details: errorMessage,
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useDeleteVmetaConfigApi = (): UseMutationResult<
  void,
  ErrorNotification,
  VmetaConfigApiRequestParams
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<void, ErrorNotification, VmetaConfigApiRequestParams>({
    mutationFn: async ({ canvasId, id }) => {
      const res = await axios.delete(
        `/api/v1/vmeta/canvas/${canvasId}/config/api/${id}`
      );
      return res.data;
    },
    onSuccess: async (_, variables): Promise<void> => {
      await queryClient.invalidateQueries({
        queryKey: [
          "/api/v1/vmeta/canvas/config/api/search",
          variables.canvasId,
        ],
      });
      setNotification({
        id: "delete-vmeta-config-api-success",
        message: "API Config Deleted",
        details: "API config has been deleted successfully.",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      const errorMessage =
        error?.response?.data?.message || "API config could not be deleted";

      setNotification({
        id: "delete-vmeta-config-api-failed",
        message: "API Config Deletion Failed",
        details: errorMessage,
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useUpdateVmetaField = (): UseMutationResult<
  VmetaFieldResponseBody,
  ErrorNotification,
  {
    _id: string;
  } & UpdateVmetaFieldRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<
    VmetaFieldResponseBody,
    ErrorNotification,
    {
      _id: string;
    } & UpdateVmetaFieldRequestBody
  >({
    mutationFn: async (
      payload: {
        _id: string;
      } & UpdateVmetaFieldRequestBody
    ) => {
      const { _id, collectionID, ...body } = payload;
      const res = await axios.patch(`/api/v1/vmeta/field/${_id}`, body);
      return res.data;
    },
    onSuccess: async (): Promise<void> => {
      await queryClient.invalidateQueries({
        queryKey: ["/api/v1/vmeta/field/search"],
      });
      setNotification({
        id: "update-vmeta-field-success",
        message: "Field Updated Successfully",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "update-vmeta-field-failed",
        message: "Field Update Failed",
        details: error?.response?.data?.message ?? "Field could not be updated",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

interface DownloadVmetaDataRequestQuery {
  canvasId: string;
  collectionName: string;
  filter?:
    | { or: Array<{ id: string; value: unknown }> }
    | { and: Array<{ id: string; value: unknown }> };
  operatorMap?: OperatorMap;
  sort?: Array<{ id: string; desc: boolean }>;
}

export const useDownloadVmetaData = (): UseMutationResult<
  AxiosResponse<Blob>,
  ErrorNotification,
  DownloadVmetaDataRequestQuery
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    AxiosResponse<Blob>,
    ErrorNotification,
    DownloadVmetaDataRequestQuery
  >({
    mutationFn: ({ canvasId, collectionName, filter, operatorMap, sort }) => {
      const baseUrl = `/api/v1/vmeta/canvas/${canvasId}/data/${collectionName}/download/populated`;
      const payload: any = {};

      // Add optional parameters if provided
      if (sort && sort.length > 0) {
        payload.sort = sort[0].id;
        payload.order = sort[0].desc ? "desc" : "asc";
      }

      // Handle filter using createMongoFilter (single source of truth)
      if (filter) {
        const mongoFilter = createMongoFilter(filter, operatorMap || {});
        payload.filter = mongoFilter;
      }

      return axios.post(baseUrl, payload, {
        responseType: "blob",
        withCredentials: true,
      });
    },
    onSuccess: (response, variables) => {
      try {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${variables.collectionName}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        setNotification({
          id: "vmeta-data-download-success",
          message: "Data exported",
          details: "Data file is downloading.",
          variant: NOTIFICATION.SUCCESS,
        });
      } catch (error) {
        console.error("Error downloading file:", error);
        setNotification({
          id: "vmeta-data-download-error",
          message: "Download failed",
          details: "Error occurred while downloading the file.",
          variant: NOTIFICATION.ERROR,
        });
      }
    },
    onError: error => {
      setNotification({
        id: "vmeta-data-download-failed",
        message: "Export failed",
        details:
          error?.response?.data?.message ?? "We couldn't export the data file.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};
